import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /**
   * gldn157 #544 — prefetch cache for connected-product (Material) switches.
   *
   * A Material change is a cross-product change: it fetches the connected
   * product's whole page and morphs all of `main`, which is the dominant cost
   * of the switch. Warming that exact request on hover / focus / pointerdown
   * means the click usually has the HTML already.
   *
   * Scoped to options carrying `data-connected-product-url` only, so ordinary
   * same-product option changes add no requests. Static (not per-instance) so a
   * warm entry survives the morph that replaces this element.
   *
   * @type {Map<string, Promise<string>>}
   */
  static #prefetched = new Map();

  /** Keep the cache small: a shopper only hovers a handful of swatches. */
  static #PREFETCH_LIMIT = 4;

  /**
   * Consumes a warmed response for this URL, if one exists. Single-use: the
   * entry is dropped on read so a later switch to the same material re-fetches
   * instead of morphing in a response whose inventory may have moved.
   * @param {string} requestUrl
   * @returns {Promise<string> | undefined}
   */
  static takePrefetched(requestUrl) {
    const hit = VariantPicker.#prefetched.get(requestUrl);
    if (hit) VariantPicker.#prefetched.delete(requestUrl);
    return hit;
  }

  /**
   * Warms the section request for a connected-product option on intent.
   * @param {HTMLElement} option - The option element the shopper signalled intent on.
   */
  #warmConnectedProduct(option) {
    if (!option.dataset.connectedProductUrl) return;
    if (option instanceof HTMLInputElement && option.checked) return;

    // No trustworthy prediction means no warm: falling back to the live `:checked`
    // values would warm a URL the click never asks for — a wasted request that can
    // only ever miss.
    const optionsValues = this.#predictOptionsValues(option);
    if (!optionsValues) return;

    const requestUrl = this.buildRequestUrl(option, null, [], { optionsValues, track: false });
    if (VariantPicker.#prefetched.has(requestUrl)) return;

    while (VariantPicker.#prefetched.size >= VariantPicker.#PREFETCH_LIMIT) {
      const oldest = VariantPicker.#prefetched.keys().next().value;
      if (oldest === undefined) break;
      VariantPicker.#prefetched.delete(oldest);
    }

    // A failed warm must never reject into an unhandled rejection, and must not
    // poison the click path — drop the entry so the click falls back to a live fetch.
    const pending = fetch(requestUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`prefetch ${response.status}`);
        return response.text();
      })
      .catch(() => {
        VariantPicker.#prefetched.delete(requestUrl);
        return '';
      });

    VariantPicker.#prefetched.set(requestUrl, pending);
  }

  /**
   * The option values the picker will report once `option` is the checked one in its
   * own fieldset — i.e. what `selectedOptionsValues` returns after the click. Built by
   * walking the same node list that getter walks, in the same DOM order, so the only
   * difference is the substitution inside `option`'s fieldset.
   * @param {HTMLElement} option
   * @returns {string[] | null} null when the prediction can't be trusted.
   */
  #predictOptionsValues(option) {
    const fieldset = option.closest('fieldset');
    const optionValueId = option.dataset.optionValueId;
    if (!fieldset || !optionValueId) return null;

    const selected = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    const values = [];
    let substituted = false;

    for (const current of selected) {
      if (!(current instanceof HTMLElement)) return null;
      if (current.closest('fieldset') === fieldset) {
        // Only the first value in this fieldset is replaced; a fieldset holds one
        // checked radio, so a second hit would mean an assumption that no longer holds.
        if (substituted) return null;
        values.push(optionValueId);
        substituted = true;
        continue;
      }
      const { optionValueId: currentId } = current.dataset;
      if (!currentId) return null;
      values.push(currentId);
    }

    return substituted ? values : null;
  }

  /**
   * Routes an intent event to the connected-product warmer.
   * @param {Event} event
   */
  #onConnectedProductIntent = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const option = target.closest('[data-connected-product-url]');
    if (option instanceof HTMLElement) this.#warmConnectedProduct(option);
  };

  connectedCallback() {
    super.connectedCallback();

    // #544 — intent signals for the Material prefetch. `pointerdown` is what buys
    // anything on touch, where there is no hover to lead the tap.
    for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
      this.addEventListener(type, this.#onConnectedProductIntent, { capture: true, passive: true });
    }

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
      this.removeEventListener(type, this.#onConnectedProductIntent, { capture: true });
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {object} [options] - gldn157 #544 additions, both defaulted to today's behavior.
   * @param {string[] | null} [options.optionsValues] - Option values to use instead of the
   *   live `:checked` state. The prefetch warms a URL before the shopper has clicked, so it
   *   has to ask for the values the click WILL produce; sharing one builder is the only way
   *   the warmed key is guaranteed to equal the key the click looks up.
   * @param {boolean} [options.track] - When false, do not record `#pendingRequestUrl`. A
   *   hover must not leave the picker believing a request is in flight.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], { optionsValues = null, track = true } = {}) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (track) this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const selectedValues = optionsValues ?? this.selectedOptionsValues;

    if (selectedValues.length && !source) {
      params.push(`option_values=${selectedValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // gldn157 #544 — a prefetched response (warmed on hover/focus/pointerdown of a
    // connected-product option, see #warmConnectedProduct) skips the network round
    // trip entirely. It cannot ride the AbortController, since the request started
    // before this one existed, so the abort is enforced below instead: a superseded
    // switch must not morph a stale cached response in.
    const prefetched = VariantPicker.takePrefetched(requestUrl);

    // gldn157 #544 — announce the in-flight window so the PDP's loading indicator can
    // stay up for as long as the request is genuinely still running rather than
    // withdrawing on a fixed timer. Announced for EVERY option fetch, not just the
    // cross-product 'main' morph: a same-product Material change costs a fetch too, and
    // the listener only consults this while its own window is open. `:end` fires on
    // success, failure AND abort, so the listener can never be left believing a
    // request is still pending.
    document.dispatchEvent(new CustomEvent('gldn:material-fetch:start'));
    const announceEnd = () => document.dispatchEvent(new CustomEvent('gldn:material-fetch:end'));

    (prefetched ?? fetch(requestUrl, { signal }).then((response) => response.text()))
      .then(async (responseText) => {
        if (signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        // INP: yield before the morph so the HTML parse and the DOM morph land in separate
        // tasks. The picker's checked state already updated synchronously (immediate
        // feedback); the morph + CSS recompute + VariantUpdateEvent below all still run in
        // order after the yield.
        await yieldToMainThread();

        // gldn157 #544 — the yield hands the main thread back, so a rapid second
        // switch can supersede this one across it. Re-check before morphing: on the
        // prefetched path the response is already in hand, which makes losing this
        // race (and morphing the previous selection's HTML over the new one) much
        // more likely than it was when every path waited on the network.
        if (signal.aborted) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
          this.#syncUrlToResolvedVariant(textContent);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(announceEnd);
  }

  /**
   * gldn173 #596 — point `?variant=` at the variant the server resolved.
   * `variantChanged` writes the clicked radio's `data-variant-id`, which Shopify
   * renders for that value against the PREVIOUS selection. When a script changes
   * a second axis (gldn-pdp-variant-enhance's size reselect: Feb/4 -> Jan -> 5),
   * that id is another stone's variant while the cart form holds the right one.
   * Only rewrites when the resolved variant is exactly the checked selection, so
   * an unavailable combination keeps Horizon's own URL.
   * @param {string} textContent - The response's selected-variant JSON.
   */
  #syncUrlToResolvedVariant(textContent) {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return;

    let variant;
    try {
      variant = JSON.parse(textContent);
    } catch {
      return;
    }
    if (!variant?.id || !Array.isArray(variant.options)) return;

    // One .variant-option per product option, in option order. Radios (buttons and
    // swatches) and the 'dropdowns' style's <select> are compared; a single-value
    // axis renders neither and matches by definition.
    const options = Array.from(this.querySelectorAll('.variant-option'));
    if (options.length !== variant.options.length) return;
    const matches = options.every((option, i) => {
      if (option.querySelector('input[type="radio"]')) {
        const checked = /** @type {HTMLInputElement | null} */ (option.querySelector('input[type="radio"]:checked'));
        return checked?.value === variant.options[i];
      }
      const select = option.querySelector('select');
      if (select) return /** @type {HTMLSelectElement} */ (select).value === variant.options[i];
      return true;
    });
    if (!matches) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variant.id)) return;
    url.searchParams.set('variant', String(variant.id));
    // After variantChanged's own deferred replaceState, which this would otherwise race.
    yieldToMainThread().then(() => history.replaceState({}, '', url.toString()));
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
